export const UPCOMING_VIEW = Object.freeze({ kind: 'upcoming' });

export const toDateStr = (d) => {
  if (!d) return '';
  const x = d instanceof Date ? d : new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export function eventsCalendar(now = new Date()) {
  const today = toDateStr(now);
  const year = now.getFullYear();
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const archiveYears = [year, year - 1, year - 2];
  const oldestYear = archiveYears[archiveYears.length - 1];
  const fetchRange = { from: `${oldestYear}-01-01`, to: yearEnd };
  return { today, year, yearStart, yearEnd, archiveYears, fetchRange };
}

function tagged(events) {
  const list = Array.isArray(events) ? events : [];
  return list.map((ev, i) => ({ ev, day: toDateStr(ev.date), i }));
}

function sortAsc(rows) {
  return [...rows]
    .sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : a.i - b.i))
    .map((row) => row.ev);
}

function sortDesc(rows) {
  return [...rows]
    .sort((a, b) => (a.day > b.day ? -1 : a.day < b.day ? 1 : a.i - b.i))
    .map((row) => row.ev);
}

function viewYear(view) {
  if (view.kind === 'year') return view.year;
  return Number(view.start.slice(0, 4));
}

function normalizeView(view, cal) {
  if (!view || view.kind === 'upcoming') return UPCOMING_VIEW;
  if (view.kind === 'year' && cal.archiveYears.includes(view.year)) {
    return { kind: 'year', year: view.year };
  }
  if (view.kind === 'week' && typeof view.start === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(view.start)) {
    const year = Number(view.start.slice(0, 4));
    if (cal.archiveYears.includes(year)) return { kind: 'week', start: view.start };
  }
  return UPCOMING_VIEW;
}

function weekLabel(start, count) {
  const [y, m, d] = start.split('-').map(Number);
  const formatted = new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  if (!count) return `${formatted} · no past meetings`;
  if (count > 1) return `${formatted} · ${count} meetings`;
  return formatted;
}

function pastInYear(rows, year, today) {
  const prefix = `${year}-`;
  return rows.filter((row) => row.day.startsWith(prefix) && row.day < today);
}

function weekMenu(view, rows, today) {
  const year = viewYear(view);
  const past = pastInYear(rows, year, today);
  const counts = new Map();
  past.forEach((row) => {
    counts.set(row.day, (counts.get(row.day) || 0) + 1);
  });
  if (view.kind === 'week' && !counts.has(view.start)) counts.set(view.start, 0);
  const dates = [...counts.keys()].sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
  const value = view.kind === 'week' ? `week:${view.start}` : `year:${year}`;
  return {
    value,
    options: [
      { value: `year:${year}`, label: 'All weeks' },
      ...dates.map((start) => ({ value: `week:${start}`, label: weekLabel(start, counts.get(start)) })),
    ],
  };
}

function hintFor(view, cal) {
  if (view.kind === 'upcoming') return `Today through Dec 31, ${cal.year}.`;
  const year = viewYear(view);
  if (year < cal.year) return `Meetings in ${year}, newest first. Read-only.`;
  return `Past meetings in ${year}, newest first. Open a meeting to correct it.`;
}

function emptyTextFor(view, cal) {
  if (view.kind === 'upcoming') {
    return `No upcoming meetings through Dec 31, ${cal.year}. Next year's meetings show here starting Jan 1.`;
  }
  if (view.kind === 'week') return 'No past meetings in this week.';
  if (view.year < cal.year) return `No meetings recorded in ${view.year}.`;
  return 'No past meetings yet this year.';
}

export function eventsPanel(events, view, cal) {
  const active = normalizeView(view, cal);
  const rows = tagged(events);
  const upcoming = rows.filter((row) => row.day >= cal.today && row.day <= cal.yearEnd);
  let visible = upcoming;
  if (active.kind === 'year') visible = pastInYear(rows, active.year, cal.today);
  if (active.kind === 'week') {
    visible = rows.filter((row) => row.day === active.start && row.day < cal.today);
  }
  const readOnly = active.kind !== 'upcoming' && viewYear(active) < cal.year;
  const showValue = active.kind === 'upcoming' ? 'upcoming' : `year:${viewYear(active)}`;
  return {
    view: active,
    rows: active.kind === 'upcoming' ? sortAsc(visible) : sortDesc(visible),
    readOnly,
    upcomingCount: upcoming.length,
    hint: hintFor(active, cal),
    emptyText: emptyTextFor(active, cal),
    menu: {
      show: {
        value: showValue,
        upcoming: { value: 'upcoming', label: 'Upcoming meetings' },
        archive: cal.archiveYears.map((y) => ({
          value: `year:${y}`,
          label: y === cal.year ? `${y} · past weeks (editable)` : `${y} · read-only`,
        })),
      },
      week: active.kind === 'upcoming' ? null : weekMenu(active, rows, cal.today),
    },
  };
}

export function parseEventsView(value) {
  if (value === 'upcoming') return UPCOMING_VIEW;
  if (typeof value !== 'string') return UPCOMING_VIEW;
  const year = /^year:(\d{4})$/.exec(value);
  if (year) return { kind: 'year', year: Number(year[1]) };
  const week = /^week:(\d{4}-\d{2}-\d{2})$/.exec(value);
  if (week) return { kind: 'week', start: week[1] };
  return UPCOMING_VIEW;
}
